import React, { useState } from 'react';
import styled from 'styled-components';
import { Icon, Label } from 'semantic-ui-react';
import { get, has, isEmpty } from 'lodash';
import { Symbol } from './symbol';


// Storybook reference: https://ui-standard.hackhub.com/?path=/story/styleguide-uicomponents-inputs--input-group

export const Input = ({
  field, form: {
    setFieldValue, values, touched, errors, setTouched
  }, otherProps, ...props
}) => {
  const {
    id,
    label,
    type,
    placeholder,
    hint,
    showPopUp
  } = otherProps;
  const [value, setValue] = useState(isEmpty(get(values, `${field.name}`)) ? '' : get(values, `${field.name}`));
  const [passwordShown, setPasswordShown] = useState(false);
  const [typeToggled, setTypeToggled] = useState('password');
  const hasError = has(touched, field.name) && has(errors, field.name);

  const handleOnChange = e => {
    if (!has(touched, field.name) && setTouched) {
      setTouched({
        ...touched,
        [field.name]: true
      });
    }
    setValue(e.target.value);
    setFieldValue(field.name, e.target.value);
  };

  const togglePasswordShown = () => {
    setPasswordShown(!passwordShown);
    if (typeToggled === 'password') {
      setTypeToggled('text');
    } else {
      setTypeToggled('password');
    }
  };


  const lengthRegex = /^.{6,30}$/;
  const intRegex = /[0-9]/;
  const lowerCaseRegex = /[a-z]/;
  const upperCaseRegex = /[A-Z]/;
  const specialCharRegex = /\W/;

  if (type === 'password') {
    return (
      <Block type={type}>
        { label && (<StyledLabel>{label}</StyledLabel>)}
        <div>
          <InputStyle
            id={id}
            type={typeToggled}
            value={value}
            placeholder={placeholder}
            onChange={handleOnChange}
            {...props}
          />

          {type === 'password' && (
            <VisibilityIcon
              name={passwordShown ? 'eye' : 'eye slash'}
              onClick={togglePasswordShown}
            />
          )}
        </div>
        {
          hasError && showPopUp && showPopUp === 'passwordInput' && (
            <ValidationCheckLabel
              basic pointing='left'
            >
              <span>Password must include:</span>
              <ul>
                <li>
                  <Symbol iconName='check' color={lengthRegex.test(value) ? '#11AA66' : '#99B2C6'} />
                  Length must be from 6 to 30</li>
                <li>  <Symbol iconName='check' color={intRegex.test(value) ? '#11AA66' : '#99B2C6'} />At least one number</li>
                <li>  <Symbol iconName='check' color={upperCaseRegex.test(value) ? '#11AA66' : '#99B2C6'} />At least one uppercase</li>
                <li>  <Symbol iconName='check' color={lowerCaseRegex.test(value) ? '#11AA66' : '#99B2C6'} />At least one lowercase</li>
                <li>
                  <Symbol iconName='check' color={specialCharRegex.test(value) ? '#11AA66' : '#99B2C6'} />
                  At least one special symbol</li>
              </ul>
            </ValidationCheckLabel>
          )
        }
        {
          hint && (
            <HintText>{hint}</HintText>
          )
        }
        {
          hasError && (
            <ErrorText>{errors[field.name]}</ErrorText>
          )
        }
      </Block>
    );
  }
  return (
    <Block type={type}>
      <StyledLabel>{label}</StyledLabel>
      <InputStyle
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={handleOnChange}
        error={hasError}
        {...props}
      />
      {
        hasError && (
          <ErrorText>{errors[field.name]}</ErrorText>
        )
      }
    </Block>
  );
};
  /* width: 320px; */
const Block = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  div {
    position: relative;
    width: 100%;
  }

  label, small {
    font-family: 'Inter';
    font-style: normal;
  }

  label {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #344054;
  }
`;

const ValidationCheckLabel = styled(Label)`
&&&&& {
  position: absolute;
  top: -40px;
  right: -230px;
  width: 220px;
  padding: 10px;
  font-family: Inter;
  background: #fff;

  @media(max-width: 880px) {
    top: -130px;
    left: 0px;
    right: 0;

    &:before {
      border-width: 0 0 1px 1px;
      transform: translateX(0%) translateY(-25%) rotate(315deg);
      bottom: auto;
      top: 100%;
      left: 50%;
    }
  }

  span:first-of-type {
    display: inline-block;
    padding: 4px 0;
    font-size: 12px;
  }

  ul {
    list-style:none;
  }

  li {
    font-size: 12px;
    padding: 1px 0;
    font-weight: normal;
    span {
      width: 20px;
      height: 20px;

      i {
        font-size: 1em;
      }
    }
  }
}
`;


const StyledLabel = styled.label`
&&&& {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  color: #384455 !important;
}
`;

const InputStyle = styled.input.attrs(props => ({
  type: props.type
}))`
&&&&& {
 padding: 0.5em;
  width: 100%;
  margin: 0.5em 0;
  font-size: 16px;
  font-family: 'Inter';
  font-weight: 400;
  background: #ffffff;
  line-height: 24px;
  border: 1px solid #99b2c6;
  border-radius: 8px;
  outline: none;
  color: #101828;
  position: relative;

  &::placeholder {
    color: #809FB8 !important;
    font-family: 'Quicksand';
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
  }
  :disabled {
    background: #f1f4f9;
  }
  :focus {
    border: 1px solid #0094fb;
  }
  :invalid {
    border: 1px solid #ea3a3d;
  }
}
  
`;

const VisibilityIcon = styled(Icon)`
  color: #99b2c6;
  width: 18px;
  height: 18px;
  position: absolute;
  top: 50%;
  right: 0.2em;
  transform: translateY(-50%);
  cursor: pointer;
`;

const HintText = styled.small`
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #667085;
`;

const ErrorText = styled(HintText)`
  color: #F04438;
`;


// Input.defaultProps = {
//   label: 'Label',
//   type: 'text',
//   placeholder: 'Placeholder'
// };



// WEBPACK FOOTER //
// ./src/uiStandard/event/input.js